import React, { useCallback, useEffect, useState, JSX } from 'react';
import classNames from 'classnames';
import { IBoxChooserComponent, IBoxComponent } from '@enuan/chat__utils';
import { FormRow } from './FormRow';
import { styled } from '@mui/material/styles';
import { cleanBoxProps } from '../../../utils/cleanBoxProps';

type Props = IBoxComponent<
	IBoxChooserComponent & Omit<IChooserItemProps, 'value' | 'onChange'>
> & {
	onChange?: (value: string) => void;
};

export const Chooser = styled(
	({
		// custom props
		type,
		help_label,
		error,
		error_label,
		required_label,
		label,
		onChange,
		group,

		// standard props
		id, //ignore it to avoid duplicate
		options,
		value = '',
		className,
		required,
	}: Props) => {
		const [_value, setValue] = useState(value);
		useEffect(() => {
			setValue(value);
		}, [value]);

		return (
			<FormRow
				className={classNames('form__row--chooser', className)}
				label={label}
				error={error}
				error_label={error_label}
				help_label={help_label}
				required={required}
				required_label={required_label}
			>
				<div className="chooser">
					{options.map(({ value, label }, i) => (
						<ChooserItem
							key={i}
							label={label}
							onChange={() => {
								onChange?.(value);
								setValue(value);
							}}
							checked={_value === value}
							group={group}
						/>
					))}
				</div>
			</FormRow>
		);
	},
	{
		label: 'Chooser',
		target: 'Chooser-root',
	},
)({
	'& .chooser': {
		display: 'flex',
		flex: 1,
		flexDirection: 'row',
		position: 'relative',
		alignItems: 'center',
	},
	'& .chooser__item': {
		flex: 1,
		margin: 0,
		marginRight: '1px',
		background: 'var(--form__chooser__item_background)',
		textAlign: 'center',
		fontFamily: 'Roboto, sans-serif',
		fontWeight: 100,
		color: 'var(--form__chooser__item_color)',
		fontSize: '1.5em',
		'&:not(.chooser__item--disabled) .chooser__label': {
			cursor: 'pointer',
		},
		'&:not(.chooser__item--disabled):not(.chooser__item--checked)': {
			transition: 'all 150ms',
			'&:hover': {
				background: 'var(--form__chooser__item--hover_background)',
				color: 'var(--form__chooser__item--hover_color)',
			},
		},
		'&.chooser--checked': {
			background: 'var(--form__chooser__item--checked__label_background)',
			color: 'var(--form__chooser__item--checked__label_color)',
		},
		'&:first-child': {
			borderRadius: '4px 0 0 4px',
		},
		'&:last-child': {
			borderRadius: '0 4px 4px 0',
			marginRight: 0,
		},
		'& .chooser__input': {
			position: 'absolute',
			left: '-9999px',
			opacity: 0,
		},
		'& .chooser__label': {
			display: 'block',
			padding: '6px',
		},
	},
});

type IChooserItemProps = Omit<
	JSX.IntrinsicElements['input'],
	'onChange' | 'value' | 'type'
> & {
	onChange?: (value: boolean) => void;
	label: string;
	error?: string | boolean;
	value?: JSX.IntrinsicElements['input']['checked'];
	group: string;
};
const ChooserItem = ({
	group,
	checked,
	onChange,
	label,
	disabled,
	className,
	error,
	value,
	..._props
}: IChooserItemProps) => {
	const props = cleanBoxProps(_props);
	const val = checked ?? value ?? false;
	const [_checked, setChecked] = useState(val);
	useEffect(() => {
		setChecked(val);
	}, [val]);

	const _onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
		({ currentTarget: { checked } }) => {
			setChecked(checked);
			onChange?.(checked);
		},
		[onChange],
	);
	const handleClick = useCallback(() => {
		if (disabled) return;
		setChecked(true);
		onChange?.(true);
	}, [disabled, onChange]);

	return (
		<div
			className={classNames(
				className,
				'chooser__item',
				!!error && 'color--danger',
				checked && 'chooser__item--checked',
				disabled && 'chooser__item--disabled',
			)}
		>
			<input
				className="chooser__input"
				type="radio"
				onChange={_onChange}
				name={group}
				checked={_checked}
				disabled={disabled}
				{...props}
			/>
			<label className="chooser__label" onClick={handleClick}>
				<span>{label}</span>
			</label>
		</div>
	);
};
